import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  Cloud,
  Copy,
  ExternalLink,
  FileSpreadsheet,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Search,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  chooseOneDriveFile,
  disconnectOneDrive,
  getOneDriveStatus,
  searchOneDriveFiles,
  setOneDriveLiveSync,
  startOneDriveConnect,
  syncOneDriveNow,
} from "@/services/dataService";
import { KIND_DESTINATIONS, KIND_LABELS } from "@/lib/workbookImport";

function timeAgo(timestamp) {
  if (!timestamp) return "never";
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const isSyncing = (connection) =>
  connection?.status === "syncing" ||
  Boolean(connection?.syncStartedAt && Date.now() - connection.syncStartedAt < 3 * 60 * 1000);

export default function OneDriveSync() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [choosing, setChoosing] = useState(false);

  const statusQuery = useQuery({
    queryKey: ["oneDriveStatus", userId],
    queryFn: getOneDriveStatus,
    enabled: Boolean(userId),
    refetchInterval: (query) => (isSyncing(query.state.data?.connection) ? 3000 : 30000),
  });
  const status = statusQuery.data;
  const connection = status?.connection;

  // Coming back from Microsoft sign-in.
  useEffect(() => {
    const outcome = searchParams.get("onedrive");
    if (!outcome) return;
    if (outcome === "connected") {
      toast({ title: "OneDrive connected", description: "Now choose the workbook to keep in sync." });
    } else {
      toast({
        title: "OneDrive wasn't connected",
        description: searchParams.get("message") || "Please try again.",
        variant: "destructive",
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("onedrive");
    next.delete("message");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["oneDriveStatus", userId] });
    queryClient.invalidateQueries({ queryKey: ["workbookImport"] });
  };
  const onError = (error) =>
    toast({ title: "Something went wrong", description: error.message, variant: "destructive" });

  const connect = useMutation({
    mutationFn: startOneDriveConnect,
    onSuccess: ({ url }) => window.location.assign(url),
    onError,
  });
  const choose = useMutation({
    mutationFn: chooseOneDriveFile,
    onSuccess: () => {
      setChoosing(false);
      refresh();
    },
    onError,
  });
  const live = useMutation({
    mutationFn: setOneDriveLiveSync,
    onSuccess: (_, enabled) => {
      toast({
        title: enabled ? "Live sync is on" : "Live sync paused",
        description: enabled
          ? `Changes in the workbook will show up here within about ${status?.intervalMinutes ?? 5} minutes.`
          : "Nothing will change until you turn it back on.",
      });
      refresh();
    },
    onError,
  });
  const syncNow = useMutation({ mutationFn: syncOneDriveNow, onSuccess: refresh, onError });
  const disconnect = useMutation({
    mutationFn: disconnectOneDrive,
    onSuccess: () => {
      toast({ title: "OneDrive disconnected", description: "Everything already copied stays in your workspace." });
      refresh();
    },
    onError,
  });

  let body;
  if (statusQuery.isLoading || !status) {
    body = statusQuery.error ? (
      /could not find (public )?function/i.test(statusQuery.error.message) ? (
        <BackendPending />
      ) : (
        <p className="text-sm text-destructive">Couldn't load sync status: {statusQuery.error.message}</p>
      )
    ) : (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking…
      </p>
    );
  } else if (!status.configured) {
    body = <SetupSteps redirectUri={status.redirectUri} />;
  } else if (!connection) {
    body = (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Sign in with the Microsoft account that holds your workbook. Highlander Homes gets read-only access — it
          never changes your files.
        </p>
        <Button onClick={() => connect.mutate()} disabled={connect.isPending} className="gap-2">
          {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
          Connect OneDrive
        </Button>
      </div>
    );
  } else if (connection.status === "reconnect") {
    body = (
      <div className="space-y-3">
        <Problem>
          {connection.lastError || "Microsoft needs you to sign in again."} Syncing is paused until you reconnect.
        </Problem>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => connect.mutate()} disabled={connect.isPending} className="gap-2">
            <Cloud className="h-4 w-4" /> Reconnect OneDrive
          </Button>
          <DisconnectButton mutation={disconnect} />
        </div>
      </div>
    );
  } else if (!connection.hasFile || choosing) {
    body = (
      <FilePicker
        account={connection.accountEmail || connection.accountName}
        onChoose={(file) => choose.mutate({ fileId: file.id, fileName: file.name, fileUrl: file.webUrl })}
        choosing={choose.isPending}
        onCancel={connection.hasFile ? () => setChoosing(false) : null}
        footer={!connection.hasFile ? <DisconnectButton mutation={disconnect} /> : null}
      />
    );
  } else {
    const syncing = isSyncing(connection);
    const preview = !connection.liveSync;
    body = (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{connection.fileName}</span>
          {connection.fileUrl ? (
            <a
              href={connection.fileUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-muted-foreground underline-offset-2 hover:underline"
            >
              Open in OneDrive <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
          {connection.liveSync ? (
            <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" variant="secondary">
              Live
            </Badge>
          ) : (
            <Badge variant="secondary">Preview — nothing synced yet</Badge>
          )}
        </div>

        {connection.liveSync ? (
          <p className="text-sm text-muted-foreground">
            {syncing ? "Syncing now…" : `Last change synced ${timeAgo(connection.lastSyncedAt)}`} · checked{" "}
            {timeAgo(connection.lastCheckedAt)} · checks every {status.intervalMinutes} minutes
            {connection.accountEmail ? ` · ${connection.accountEmail}` : ""}
          </p>
        ) : null}

        {connection.status === "error" && connection.lastError ? <Problem>{connection.lastError}</Problem> : null}

        {syncing && !connection.summary ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading your workbook…
          </p>
        ) : connection.summary ? (
          <SyncSummary summary={connection.summary} preview={preview} />
        ) : null}

        <div className="flex flex-wrap gap-2">
          {preview ? (
            <Button
              onClick={() => live.mutate(true)}
              disabled={live.isPending || syncing || !connection.summary}
              className="gap-2"
            >
              <Play className="h-4 w-4" /> Turn on live sync
            </Button>
          ) : null}
          <Button
            variant="outline"
            onClick={() => syncNow.mutate()}
            disabled={syncNow.isPending || syncing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {preview ? "Refresh preview" : "Sync now"}
          </Button>
          {connection.liveSync ? (
            <Button variant="outline" onClick={() => live.mutate(false)} disabled={live.isPending} className="gap-2">
              <Pause className="h-4 w-4" /> Pause
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => setChoosing(true)}>
            Choose a different file
          </Button>
          <DisconnectButton mutation={disconnect} />
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-500/10 text-sky-700 dark:text-sky-300">
            <Cloud className="h-5 w-5" />
          </span>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">Live sync with OneDrive</h2>
            <p className="text-sm text-muted-foreground">
              Keep working in your Excel workbook. Changes show up here automatically — new rows are added, edited
              rows are updated and deleted rows are removed. Things you add directly on this site are never removed.
            </p>
          </div>
        </div>
        {body}
      </CardContent>
    </Card>
  );
}

/** Shown until the Convex server code for this page has been published. */
function BackendPending() {
  return (
    <div className="space-y-2 text-sm">
      <Problem>The server part of this page hasn't been published yet, so syncing and importing won't work yet.</Problem>
      <p className="font-medium">To publish it (no computer setup needed):</p>
      <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
        <li>
          In the{" "}
          <a className="underline" href="https://dashboard.convex.dev" target="_blank" rel="noreferrer">
            Convex dashboard
          </a>
          , open the highlander-homes project, then the deployment named{" "}
          <code className="rounded bg-muted px-1 text-xs text-foreground">successful-goldfinch-551</code> →{" "}
          <strong>Settings → URL &amp; Deploy Key</strong> → generate a <strong>deploy key</strong> and copy it.
        </li>
        <li>
          In{" "}
          <a className="underline" href="https://vercel.com/paukuntong/highlanderhomes/settings/environment-variables" target="_blank" rel="noreferrer">
            Vercel → highlanderhomes → Settings → Environment Variables
          </a>
          , add <code className="rounded bg-muted px-1 text-xs text-foreground">CONVEX_DEPLOY_KEY</code> with that
          value, for <strong>Production</strong> only.
        </li>
        <li>
          In Vercel → Deployments, open the latest production deployment and press <strong>Redeploy</strong>.
        </li>
      </ol>
    </div>
  );
}

function Problem({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p>{children}</p>
    </div>
  );
}

function DisconnectButton({ mutation }) {
  return (
    <Button
      variant="ghost"
      className="text-muted-foreground"
      disabled={mutation.isPending}
      onClick={() => {
        if (window.confirm("Stop syncing and disconnect OneDrive? Everything already copied stays in your workspace.")) {
          mutation.mutate();
        }
      }}
    >
      Disconnect
    </Button>
  );
}

function SyncSummary({ summary, preview }) {
  const verbs = preview
    ? { created: "to add", updated: "to update", removed: "to remove" }
    : { created: "added", updated: "updated", removed: "removed" };
  const counts = summary.counts || [];
  const nothingToDo = counts.every((row) => !row.created && !row.updated && !row.removed);
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">
        {preview
          ? "Here's what live sync would do. Nothing has been changed yet."
          : nothingToDo
            ? "Everything is up to date."
            : "Last sync:"}
      </p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {counts.map((row) => (
          <Link
            key={row.kind}
            to={KIND_DESTINATIONS[row.kind]}
            className="rounded-lg border p-3 text-sm transition-colors hover:bg-muted/50"
          >
            <span className="font-medium">{KIND_LABELS[row.kind]}</span>
            <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">
              {row.created ? <span className="text-emerald-700 dark:text-emerald-300">{row.created} {verbs.created}</span> : null}
              {row.updated ? <span className="text-sky-700 dark:text-sky-300">{row.updated} {verbs.updated}</span> : null}
              {row.removed ? <span className="text-rose-700 dark:text-rose-300">{row.removed} {verbs.removed}</span> : null}
              <span>{row.unchanged} already in step</span>
              {row.needsInfo ? <span className="text-amber-700 dark:text-amber-300">{row.needsInfo} need info</span> : null}
            </span>
            {row.heldBack ? (
              <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
                Held back removing {row.heldBack} — that's more than half the list. If you really deleted them from the
                sheet, delete them on this page too.
              </span>
            ) : null}
          </Link>
        ))}
      </div>
      {summary.newProperties?.length ? (
        <p className="text-sm text-muted-foreground">
          {preview ? "Would add" : "Added"} to Properties: {summary.newProperties.join(", ")}
        </p>
      ) : null}
      {summary.issues?.length ? (
        <details className="rounded-lg border p-3 text-sm">
          <summary className="cursor-pointer font-medium">
            {summary.issues.length} row{summary.issues.length === 1 ? "" : "s"} left out — fix in the workbook
          </summary>
          <ul className="mt-2 space-y-1 text-muted-foreground">
            {summary.issues.map((issue) => (
              <li key={`${issue.sheet}-${issue.line}-${issue.message}`}>
                {issue.sheet ? (
                  <span className="font-medium text-foreground">
                    {issue.sheet}
                    {issue.line ? ` row ${issue.line}` : ""}:{" "}
                  </span>
                ) : null}
                {issue.message}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {summary.ignoredSheets?.length ? (
        <p className="text-xs text-muted-foreground">
          Not synced (layout not recognised): {summary.ignoredSheets.join(", ")}
        </p>
      ) : null}
    </div>
  );
}

function FilePicker({ account, onChoose, choosing, onCancel, footer }) {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const filesQuery = useQuery({
    queryKey: ["oneDriveFiles", submitted],
    queryFn: () => searchOneDriveFiles(submitted),
    retry: false,
  });
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {account ? `Connected as ${account}. ` : ""}Choose the workbook to keep in sync.
      </p>
      <form
        className="flex max-w-lg gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(query.trim());
        }}
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search your OneDrive, e.g. Business Transaction"
            className="pl-9"
            aria-label="Search OneDrive for a workbook"
          />
        </div>
        <Button type="submit" variant="outline">
          Search
        </Button>
      </form>

      {filesQuery.error ? <Problem>{filesQuery.error.message}</Problem> : null}

      {filesQuery.isFetching ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Looking in OneDrive…
        </p>
      ) : filesQuery.data?.length ? (
        <ul className="divide-y rounded-lg border">
          {filesQuery.data.map((file) => (
            <li key={file.id} className="flex flex-col gap-2 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="truncate font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {file.folder || "OneDrive"} · edited {timeAgo(file.modifiedAt)}
                </p>
              </div>
              <Button size="sm" onClick={() => onChoose(file)} disabled={choosing} className="gap-1">
                <Check className="h-4 w-4" /> Use this file
              </Button>
            </li>
          ))}
        </ul>
      ) : filesQuery.isSuccess ? (
        <p className="text-sm text-muted-foreground">No Excel workbooks found{submitted ? ` for “${submitted}”` : ""}.</p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        {footer}
      </div>
    </div>
  );
}

function SetupSteps({ redirectUri }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-3 text-sm">
      <p className="font-medium">One-time setup (about 10 minutes)</p>
      <p className="text-muted-foreground">
        Microsoft needs to know this website is allowed to read your OneDrive. You only do this once.
      </p>
      <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
        <li>
          Go to{" "}
          <a className="underline" href="https://entra.microsoft.com" target="_blank" rel="noreferrer">
            entra.microsoft.com
          </a>{" "}
          (or portal.azure.com) and sign in with the Microsoft account that holds your workbook. Open{" "}
          <strong>App registrations → New registration</strong>.
        </li>
        <li>
          Name it “Highlander Homes sync”. Under supported account types pick{" "}
          <strong>Personal Microsoft accounts only</strong>. Under Redirect URI choose <strong>Web</strong> and paste:
          <span className="mt-1 flex items-center gap-2">
            <code className="break-all rounded bg-muted px-2 py-1 text-xs text-foreground">{redirectUri}</code>
            <Button
              size="sm"
              variant="outline"
              className="h-7 shrink-0 gap-1"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(redirectUri);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch {
                  setCopied(false);
                }
              }}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} Copy
            </Button>
          </span>
        </li>
        <li>
          After it's created, copy the <strong>Application (client) ID</strong>. Then open{" "}
          <strong>Certificates &amp; secrets → New client secret</strong> and copy the secret's <strong>Value</strong>.
        </li>
        <li>
          In the Convex dashboard (highlander-homes → Settings → Environment Variables) add{" "}
          <code className="rounded bg-muted px-1 text-xs text-foreground">MICROSOFT_CLIENT_ID</code> and{" "}
          <code className="rounded bg-muted px-1 text-xs text-foreground">MICROSOFT_CLIENT_SECRET</code> with those two
          values.
        </li>
        <li>Reload this page and press Connect OneDrive.</li>
      </ol>
    </div>
  );
}
